import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CANCEL_SUBSCRIPTION,
  GET_MY_SUBSCRIPTIONS,
} from "../../../queries/mutation/rein-tips/subscriptionQueries";
import LoadingPage from "../../LoadingPage";

type Subscription = {
  id: string;
  itemType: string;
  itemName?: string | null;
  cadence: string;
  dayOfWeek?: number | null;
  timeOfDay?: string | null;
  price: number;
  // What subscribing charged, when it differed from the renewal price.
  firstChargePrice?: number | null;
  status: string;
  nextRenewalAt?: string | null;
  lastRenewalAt?: string | null;
  cancelledAt?: string | null;
  cancelReason?: string | null;
  createdAt: string;
};

const CANCEL_REASON_LABEL: Record<string, string> = {
  user_requested: "You cancelled this",
  insufficient_funds: "Cancelled - not enough crimcoins at renewal",
  item_unavailable: "Cancelled - item no longer available",
};

const WEEKDAY_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// timeOfDay is "HH:mm" 24-hour, stored in Crim's own timezone (Central) -
// just reformatting to 12-hour for display, not converting timezones.
const formatTimeOfDay = (timeOfDay: string) => {
  const [hourStr, minute] = timeOfDay.split(":");
  const hour = Number(hourStr);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute} ${period}`;
};

const CENTRAL_TZ = "America/Chicago";
const centralDayKey = new Intl.DateTimeFormat("en-US", {
  timeZone: CENTRAL_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const centralTime = new Intl.DateTimeFormat("en-US", {
  timeZone: CENTRAL_TZ,
  hour: "numeric",
  minute: "2-digit",
});
const centralWeekdayDate = new Intl.DateTimeFormat("en-US", {
  timeZone: CENTRAL_TZ,
  weekday: "long",
  month: "short",
  day: "numeric",
});

// A plain date (no time) reads as ambiguous for a subscription that can
// renew today, later today, at a specific chosen time - e.g. subscribing
// this morning for "every Tuesday at 10:20am" makes today itself the first
// renewal, which just showing "9/15/2026" doesn't convey. Always shown in
// Central, matching what the day/time picker itself is in - not the
// viewer's own browser timezone, so it stays consistent with "Every
// Tuesday at 10:20 AM (Central)" shown next to it.
const formatRenewalDateTime = (iso: string) => {
  const date = new Date(iso);
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);

  const targetDay = centralDayKey.format(date);
  const time = centralTime.format(date);

  if (targetDay === centralDayKey.format(now)) {
    return `Today at ${time} (Central)`;
  }
  if (targetDay === centralDayKey.format(tomorrow)) {
    return `Tomorrow at ${time} (Central)`;
  }
  return `${centralWeekdayDate.format(date)} at ${time} (Central)`;
};

const MySubscriptions = () => {
  const navigate = useNavigate();
  const { data, loading } = useQuery(GET_MY_SUBSCRIPTIONS, {
    fetchPolicy: "cache-and-network",
  });

  const [cancelMutation, { loading: cancelling }] = useMutation(
    CANCEL_SUBSCRIPTION,
    {
      refetchQueries: [{ query: GET_MY_SUBSCRIPTIONS }],
      onError: (error: ApolloError) => toast.error(error.message),
    },
  );

  if (loading) {
    return <LoadingPage />;
  }

  const subscriptions: Subscription[] = data?.getMySubscriptions ?? [];
  const active = subscriptions.filter((s) => s.status === "active");
  const inactive = subscriptions.filter((s) => s.status !== "active");

  const handleCancel = async (subscription: Subscription) => {
    if (
      !window.confirm(
        `Cancel your ${subscription.cadence} subscription to ${subscription.itemName ?? subscription.itemType.replace(/_/g, " ")}?`,
      )
    ) {
      return;
    }
    const result = await cancelMutation({
      variables: { subscriptionId: subscription.id },
    });
    if (result?.data?.cancelSubscription) {
      toast.success("Subscription cancelled");
    }
  };

  return (
    <div className="min-h-screen bg-base-200 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-base-content mb-2">
            My Subscriptions
          </h1>
          <p className="text-base-content/70">
            Recurring auto-redeems - each cycle deducts crimcoins and
            redeems the item automatically.
          </p>
          <button
            onClick={() => navigate("/crimson-menu")}
            className="mt-4 bg-purple-600 hover:bg-purple-700 text-base-content font-semibold py-2 px-4 rounded transition-colors duration-200"
          >
            💜 Browse the Tip Menu
          </button>
        </div>

        {active.length === 0 ? (
          <div className="bg-yellow-900 border border-yellow-700 rounded-lg p-6 mb-8">
            <p className="text-yellow-100 text-lg text-center">
              No active subscriptions yet. Subscribable items show a
              "Subscribe & Save" option on the tip menu.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {active.map((subscription) => (
              <div
                key={subscription.id}
                // base-100 and base-200 are nearly identical in lightness in
                // this DaisyUI theme (0.253 vs 0.233 - barely 2% apart), so a
                // plain border-base-300 card was almost invisible against
                // the page. indigo-500 is a fixed palette color (not a
                // theme-derived OKLCH token), so it reads clearly as a
                // border in both light and dark theme without needing
                // separate dark:/light: classes.
                className="bg-base-100 rounded-lg border-2 border-indigo-500/40 shadow-lg shadow-black/30 p-6"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-xl font-bold text-base-content capitalize">
                      {subscription.itemName ?? subscription.itemType.replace(/_/g, " ")}
                    </h3>
                    <span className="inline-block mt-1 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-900 text-indigo-300 border border-indigo-700 capitalize">
                      🔁 {subscription.cadence}
                    </span>
                    {subscription.cadence === "weekly" &&
                      subscription.dayOfWeek != null &&
                      subscription.timeOfDay && (
                        <p className="text-xs text-base-content/50 mt-1">
                          Every {WEEKDAY_LABELS[subscription.dayOfWeek]} at{" "}
                          {formatTimeOfDay(subscription.timeOfDay)} (Central)
                        </p>
                      )}
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-purple-400">
                      {subscription.price.toLocaleString()}
                    </div>
                    <div className="text-xs text-base-content/50">
                      Crimcoins / cycle
                    </div>
                    {subscription.firstChargePrice != null &&
                      subscription.firstChargePrice !== subscription.price && (
                        <div className="text-xs text-base-content/50">
                          Started at{" "}
                          {subscription.firstChargePrice.toLocaleString()}
                        </div>
                      )}
                  </div>
                </div>

                {subscription.nextRenewalAt && (
                  <p className="text-sm text-base-content/70">
                    Next renewal:{" "}
                    <span className="font-semibold text-base-content">
                      {formatRenewalDateTime(subscription.nextRenewalAt)}
                    </span>
                  </p>
                )}
                {subscription.lastRenewalAt && (
                  <p className="text-xs text-base-content/50">
                    Last renewed:{" "}
                    {formatRenewalDateTime(subscription.lastRenewalAt)}
                  </p>
                )}

                <button
                  onClick={() => handleCancel(subscription)}
                  disabled={cancelling}
                  className="mt-4 w-full bg-red-900 hover:bg-red-800 text-red-200 border border-red-700 font-semibold py-2 px-4 rounded transition-colors duration-200 disabled:opacity-50"
                >
                  Cancel Subscription
                </button>
              </div>
            ))}
          </div>
        )}

        {inactive.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-base-content mb-3">
              Past Subscriptions
            </h2>
            <div className="space-y-2">
              {inactive.map((subscription) => (
                <div
                  key={subscription.id}
                  className="bg-base-100 rounded-lg border-2 border-gray-500/30 shadow-md shadow-black/20 p-4 flex justify-between items-center opacity-75"
                >
                  <div>
                    <span className="font-semibold text-base-content capitalize">
                      {subscription.itemName ?? subscription.itemType.replace(/_/g, " ")}
                    </span>{" "}
                    <span className="text-xs text-base-content/50 capitalize">
                      ({subscription.cadence})
                    </span>
                    <p className="text-xs text-base-content/50">
                      {subscription.cancelReason
                        ? CANCEL_REASON_LABEL[subscription.cancelReason] ??
                          subscription.cancelReason
                        : "Cancelled"}
                    </p>
                  </div>
                  {subscription.cancelledAt && (
                    <span className="text-xs text-base-content/50">
                      {centralDayKey.format(new Date(subscription.cancelledAt))}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MySubscriptions;
